/**
 * The consent surface's error taxonomy. Two classes, and the split is behavioural.
 *
 * `LoyaltyConsentAuthError` means "there is nobody to record consent for". It is thrown by a WRITE
 * with no credential and by a 401 from the endpoint. It is deliberately **recoverable** rather than
 * terminal: the Auth0 SDK answers asynchronously, so "no credential right now" is an ordinary state
 * on a perfectly healthy page -- the first few hundred milliseconds of a cold one -- and the answer
 * is to ask again rather than to send the fan to a login. (It used to be an ordinary state for a
 * worse reason: a third-party script expired every `auth0_*` cookie on `DOMContentLoaded` and the
 * credential had to be waited for. See docs/subsystems/auth.md.)
 *
 * `LoyaltyConsentRequiredError` means "the fan has not consented", and it is what the gate throws
 * when it refuses a loyalty request. It is a separate class rather than the library's
 * `LoyaltyAuthError` on purpose: that one carries `requiresReauth`, which makes the UI package
 * render "Session expired — please log in again" with `retry: null` and no control at all. A consent
 * refusal is not an expired session, and nothing about re-authenticating would fix it.
 *
 * Nothing here extends the loyalty library's classes. `instanceof LoyaltyAuthError` is load-bearing
 * inside that library (which is why @drewling/urc-loyalty-api is a peer dependency of the UI
 * package), and inheriting from it would put a consent refusal inside branches written for a dead
 * credential.
 */

/** Base, so a caller can catch every consent failure with one clause. */
export class LoyaltyConsentError extends Error {
	/**
	 * @param {string} message         Human-readable summary.
	 * @param {Object} [options]       Options.
	 * @param {*}      [options.cause] Underlying error, if any.
	 */
	constructor( message, options ) {
		super( message );
		this.name = 'LoyaltyConsentError';

		if ( options && 'cause' in options ) {
			this.cause = options.cause;
		}
	}
}

/** No authenticated fan, so there is nobody whose consent could be recorded. */
export class LoyaltyConsentAuthError extends LoyaltyConsentError {
	/**
	 * @param {string} [message] Human-readable summary.
	 * @param {Object} [options] Options.
	 */
	constructor( message, options ) {
		super( message || 'No authenticated user; loyalty consent cannot be recorded.', options );
		this.name = 'LoyaltyConsentAuthError';

		/*
		 * Deliberately the same field name the loyalty library's own LoyaltyAuthError uses, and read
		 * by the same branch: useJoinFunnel's catch tests `error.requiresReauth === true` and sends
		 * the fan to sign-in rather than showing an inline "something went wrong" they can do
		 * nothing about.
		 *
		 * It is right for a WRITE, which is always a click: JoinBlock's `startJoin()` checks the
		 * credential on that click, so reaching here means it lapsed between the click and the
		 * confirm -- exactly the narrow window that branch exists for.
		 *
		 * It changes nothing for a READ. machine.js catches this class explicitly, records
		 * `no-credential` and resolves `false`; no read path consults this field.
		 */
		this.requiresReauth = true;
	}
}

/**
 * The fan has not granted loyalty consent, so this request must not be made.
 *
 * Carries the method it refused, because the gate wraps a whole client and a caller reading a log
 * needs to know which read was blocked rather than that "something" was.
 */
export class LoyaltyConsentRequiredError extends LoyaltyConsentError {
	/**
	 * @param {string} method The API method that was refused.
	 */
	constructor( method ) {
		super( `Loyalty consent has not been granted; ${ method } was not sent.` );
		this.name = 'LoyaltyConsentRequiredError';
		this.method = method;
	}
}
